import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { toast } from "@/lib/safeToast";
import {
  buildNativePsyLogPreview,
  isNativePsyLog,
  localDateLabel,
  type NativePsyLogExport,
} from "@/lib/clinical/psylogNative";
import {
  DEFAULT_PSYLOG_PATIENT,
  importNativePsyLog,
  type NativeImportCounters,
  type NativeImportMode,
} from "@/lib/clinical/psylogNativeImport";
import { AlertTriangle, CalendarDays, CheckCircle2, FileJson, Pill, ShieldCheck, Upload } from "lucide-react";

const ALICE = "Alice Gomes Freire";

function matchesAuditedExport(preview: ReturnType<typeof buildNativePsyLogPreview> | null) {
  if (!preview) return false;
  return preview.experiences === 21
    && preview.ingestions === 107
    && preview.days === 21
    && preview.patientDays === 23
    && preview.byPatient[DEFAULT_PSYLOG_PATIENT] === 103
    && preview.byPatient[ALICE] === 4
    && preview.bySubstance.Lisdexanfetamina === 42
    && preview.bySubstance.Clonidina === 30
    && preview.bySubstance.Clonazepam === 16;
}

export function PsyLogImporter() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [raw, setRaw] = useState("");
  const [fileName, setFileName] = useState("");
  const [defaultPatient, setDefaultPatient] = useState(DEFAULT_PSYLOG_PATIENT);
  const [alicePatient, setAlicePatient] = useState(ALICE);
  const [mode, setMode] = useState<NativeImportMode>("preservar");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<NativeImportCounters | null>(null);

  const data = useMemo<NativePsyLogExport | null>(() => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return isNativePsyLog(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [raw]);
  const preview = useMemo(() => data ? buildNativePsyLogPreview(data, defaultPatient, alicePatient) : null, [data, defaultPatient, alicePatient]);

  async function readFile(file?: File | null) {
    if (!file) return;
    setFileName(file.name);
    setRaw(await file.text());
    setDone(null);
  }

  async function importNow() {
    if (!user) return toast.error("Você precisa estar autenticado.");
    if (!data || !preview) return toast.error("JSON PsyLog inválido ou sem experiences.");
    const targetNames = Object.keys(preview.byPatient).join(", ");
    if (!confirm(`Importar ${preview.ingestions} registros em ${preview.days} datas (${preview.patientDays} sessões-paciente) para ${targetNames}?`)) return;
    setRunning(true);
    setDone(null);
    try {
      const result = await importNativePsyLog({ user, data, defaultPatient, alicePatient, sourceFile: fileName, mode });
      setDone(result);
      await Promise.all([
        ["patients"], ["sessions"], ["all-doses"], ["substances"], ["all-meds"], ["sub-use-all"],
        ["curves-doses"], ["psylog-events"], ["psylog-sessions"], ["psylog-substance-use"],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      toast.success(`Importação concluída: ${result.dosesCreated} doses novas; ${result.sessionsCreated} sessões diárias criadas.`);
    } catch (error: any) {
      console.error("[PsyLogImporter] import failed", error);
      toast.error(error?.message ?? "Falha ao importar PsyLog.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 md:p-5 space-y-4 border-primary/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="font-semibold flex items-center gap-2"><FileJson className="h-4 w-4 text-primary" />Importação nativa PsyLog</div>
            <p className="text-xs text-muted-foreground max-w-3xl">Selecione a exportação original. O arquivo fica no navegador e não é incorporado ao código. Data, hora, dose, unidade, paciente e preenchimento gástrico são preservados; nenhum horário de refeição é inventado.</p>
          </div>
          {preview ? <Badge variant="outline">{preview.ingestions} registros · {preview.days} datas · {preview.patientDays} sessões</Badge> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer hover:bg-muted">
            <Upload className="h-4 w-4" />Selecionar JSON PsyLog
            <input type="file" accept="application/json,.json" className="hidden" onChange={(event) => readFile(event.target.files?.[0])} />
          </label>
          {fileName ? <span className="text-xs text-muted-foreground truncate max-w-full">{fileName}</span> : null}
          {matchesAuditedExport(preview) ? <Badge className="bg-success/10 text-success border-success/30" variant="outline"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Estrutura conferida: 107 registros</Badge> : null}
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Paciente padrão quando consumerName está vazio"><Input value={defaultPatient} onChange={(event) => setDefaultPatient(event.target.value)} /></Field>
          <Field label="Mapa para consumerName Alice"><Input value={alicePatient} onChange={(event) => setAlicePatient(event.target.value)} /></Field>
          <Field label="Conflito com registro existente">
            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={mode} onChange={(event) => setMode(event.target.value as NativeImportMode)}>
              <option value="preservar">Preservar o existente</option>
              <option value="atualizar_contexto">Atualizar os campos importados</option>
            </select>
          </Field>
        </div>

        {raw && !data ? <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" />JSON inválido ou sem a lista experiences.</div> : null}

        {preview ? <Preview preview={preview} running={running} onImport={importNow} /> : null}
      </Card>

      {done ? <ImportResult result={done} /> : null}
    </div>
  );
}

function Preview({ preview, running, onImport }: { preview: ReturnType<typeof buildNativePsyLogPreview>; running: boolean; onImport: () => void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <Metric icon={<CalendarDays className="h-4 w-4" />} label="Período" value={`${localDateLabel(preview.firstAt!)}–${localDateLabel(preview.lastAt!)}`} />
        <Metric icon={<Pill className="h-4 w-4" />} label="Ingestões" value={String(preview.ingestions)} />
        <Metric icon={<CalendarDays className="h-4 w-4" />} label="Datas / sessões" value={`${preview.days} / ${preview.patientDays}`} />
        <Metric icon={<ShieldCheck className="h-4 w-4" />} label="Redose/sobreposição" value={String((preview.byLogType.redose ?? 0) + (preview.byLogType.sobreposicao ?? 0))} />
        <Metric icon={<AlertTriangle className="h-4 w-4" />} label="Alertas" value={String(preview.warningCount)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <CompactList title="Substâncias reconhecidas" rows={preview.bySubstance} />
        <CompactList title="Classificação clínica" rows={preview.byLogType} />
      </div>

      <details className="rounded-lg border group">
        <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-2"><span className="text-sm font-medium">Prévia por paciente e dia</span><Badge variant="secondary">{preview.groups.length} paciente-dias</Badge></summary>
        <div className="border-t max-h-[440px] overflow-auto divide-y">
          {preview.groups.map((group) => (
            <div key={group.key} className="p-3 text-xs space-y-1.5">
              <div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{group.localDate.split("-").reverse().join("/")} · {group.patientName}</span><span className="text-muted-foreground">{group.ingestions.length} registros · {group.ingestions[0].localTime.slice(0, 5)}–{group.ingestions.at(-1)!.localTime.slice(0, 5)}</span></div>
              <div className="text-muted-foreground">{group.ingestions.map((row) => `${row.localTime.slice(0, 5)} ${row.displayName} ${row.dose ?? "?"}${row.units ?? ""} · ${row.stomachContext.label} · ${row.logType}`).join(" | ")}</div>
              {group.warnings.length ? <div className="text-warning">Revisar: {group.warnings.join(" ")}</div> : null}
            </div>
          ))}
        </div>
      </details>

      {preview.warnings.length ? (
        <details className="rounded-lg border border-warning/30 bg-warning/5">
          <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-2"><span className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Anomalias preservadas, não corrigidas automaticamente</span><Badge variant="outline">{preview.warningCount}</Badge></summary>
          <div className="border-t border-warning/20 max-h-64 overflow-auto divide-y divide-warning/10">{preview.warnings.map((warning, index) => <div key={`${warning.at}-${index}`} className="p-2.5 text-xs"><b>{new Date(warning.at).toLocaleString("pt-BR")}</b> · {warning.substance}: {warning.message}</div>)}</div>
        </details>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-xs text-muted-foreground"><b>Importação idempotente:</b> coincidências exatas paciente + substância + timestamp são preservadas. Registros atribuídos a outro paciente não criam tratamento ativo automaticamente.</div>
        <Button onClick={onImport} disabled={running}>{running ? "Importando e validando…" : `Importar ${preview.ingestions} registros`}</Button>
      </div>
    </div>
  );
}

function ImportResult({ result }: { result: NativeImportCounters }) {
  return (
    <Card className="p-4 space-y-3 border-success/30 bg-success/5">
      <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" />Importação concluída</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
        <ResultItem label="Sessões diárias" value={`${result.sessionsCreated} novas · ${result.sessionsPreserved} preservadas`} />
        <ResultItem label="Doses" value={`${result.dosesCreated} novas · ${result.dosesUpdated} atualizadas · ${result.dosesPreserved} preservadas`} />
        <ResultItem label="Uso não prescrito" value={`${result.substanceUseCreated} novos · ${result.substanceUsePreserved} preservados`} />
        <ResultItem label="Estrutura" value={`${result.substancesCreated} substâncias · ${result.formulationsCreated} formulações · ${result.medicationsCreated} tratamentos`} />
      </div>
      {result.warnings.length ? <details className="rounded-md border bg-background"><summary className="cursor-pointer p-2.5 text-xs font-medium">Revisões clínicas pendentes ({result.warnings.length})</summary><div className="border-t max-h-64 overflow-auto divide-y">{result.warnings.map((warning) => <div key={warning} className="p-2 text-xs">{warning}</div>)}</div></details> : null}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <div className="rounded-lg border p-3 min-w-0"><div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">{icon}{label}</div><div className="font-semibold mt-1 truncate" title={value}>{value}</div></div>;
}

function CompactList({ title, rows }: { title: string; rows: Record<string, number> }) {
  return <div className="rounded-lg border p-3"><div className="text-xs font-medium mb-2">{title}</div><div className="flex flex-wrap gap-1.5">{Object.entries(rows).sort((a, b) => b[1] - a[1]).map(([name, count]) => <Badge key={name} variant="secondary" className="font-normal">{name} · {count}</Badge>)}</div></div>;
}

function ResultItem({ label, value }: { label: string; value: string }) {
  return <div className="rounded-md border bg-background p-2"><div className="text-muted-foreground">{label}</div><div className="font-medium mt-0.5">{value}</div></div>;
}
